/**
 * Group topology validation and normalization utilities.
 * 
 * Ensures groups maintain the invariant:
 * 1. Connected — all steps form one component via direct step adjacency
 * 2. Convex — no gaps; every step on a path between group members must be included
 * 3. Topologically ordered — stepIds in canonical order (deterministic)
 */

import type { GraphEditorState } from '@/graph-store/types';

type GraphData = GraphEditorState['graphData'];

/**
 * Error reasons for group validation failures
 */
export type GroupAnalysisErrorReason = 
  | 'disconnected'    // Multiple disconnected components
  | 'gap'             // Missing intermediate steps
  | 'duplicate'       // Duplicate step IDs
  | 'missing-step'    // Step ID not in operationNodes
  | 'already-grouped'; // Step already in another group

/**
 * Result of analyzing a group subgraph
 */
export type GroupAnalysisResult =
  | { ok: true; ordered: string[] }
  | {
      ok: false;
      reason: GroupAnalysisErrorReason;
      missingStepIds?: string[];
      conflictingGroupId?: string;
    };

/**
 * Build operation→operation DIRECTED adjacency from flowEdges.
 * Only A→B if A produces material that B consumes (directional).
 */
function buildDirectedAdjacency(graphData: GraphData): Map<string, Set<string>> {
  const { operationNodes, materialNodes, flowEdges } = graphData;
  const adj = new Map<string, Set<string>>();

  // Initialize
  for (const opId of Object.keys(operationNodes)) {
    adj.set(opId, new Set());
  }

  // Build material→op and op→material maps
  const matProducedBy = new Map<string, string>(); // material → op that produced it
  const matConsumedBy = new Map<string, string[]>(); // material → ops that consume it

  for (const edge of Object.values(flowEdges)) {
    if (edge.edgeKind === 'graphsLink') continue; // Skip link edges

    const sourceIsOp = operationNodes[edge.sourceId];
    const targetIsOp = operationNodes[edge.targetId];
    const sourceIsMat = materialNodes[edge.sourceId];
    const targetIsMat = materialNodes[edge.targetId];

    // op → material (production)
    if (sourceIsOp && targetIsMat) {
      matProducedBy.set(edge.targetId, edge.sourceId);
    }

    // material → op (consumption)
    if (sourceIsMat && targetIsOp) {
      if (!matConsumedBy.has(edge.sourceId)) {
        matConsumedBy.set(edge.sourceId, []);
      }
      matConsumedBy.get(edge.sourceId)!.push(edge.targetId);
    }
  }

  // Build DIRECTED adjacency: op1 → op2 if op1 produces a material that op2 consumes
  for (const [matId, producers] of matProducedBy.entries()) {
    const consumers = matConsumedBy.get(matId) ?? [];
    for (const consumer of consumers) {
      adj.get(producers)?.add(consumer); // Only one direction!
    }
  }

  return adj;
}

/**
 * Build operation→operation adjacency from flowEdges.
 * Two ops are directly connected if output of one feeds input of the other.
 */
export function buildStepAdjacency(graphData: GraphData): Map<string, Set<string>> {
  const { operationNodes, materialNodes, flowEdges } = graphData;
  const adj = new Map<string, Set<string>>();

  // Initialize
  for (const opId of Object.keys(operationNodes)) {
    adj.set(opId, new Set());
  }

  // Build material→op and op→material maps
  const matProducedBy = new Map<string, string>(); // material → op that produced it
  const matConsumedBy = new Map<string, string[]>(); // material → ops that consume it

  for (const edge of Object.values(flowEdges)) {
    if (edge.edgeKind === 'graphsLink') continue; // Skip link edges

    const sourceIsOp = operationNodes[edge.sourceId];
    const targetIsOp = operationNodes[edge.targetId];
    const sourceIsMat = materialNodes[edge.sourceId];
    const targetIsMat = materialNodes[edge.targetId];

    // op → material (production)
    if (sourceIsOp && targetIsMat) {
      matProducedBy.set(edge.targetId, edge.sourceId);
    }

    // material → op (consumption)
    if (sourceIsMat && targetIsOp) {
      if (!matConsumedBy.has(edge.sourceId)) {
        matConsumedBy.set(edge.sourceId, []);
      }
      matConsumedBy.get(edge.sourceId)!.push(edge.targetId);
    }
  }

  // Build adjacency: op1 → op2 if op1 produces a material that op2 consumes
  for (const [matId, producers] of matProducedBy.entries()) {
    const consumers = matConsumedBy.get(matId) ?? [];
    for (const consumer of consumers) {
      adj.get(producers)?.add(consumer);
      adj.get(consumer)?.add(producers); // Undirected for connectivity check
    }
  }

  return adj;
}

/**
 * Check if two nodes are in the same connected component (undirected BFS)
 */
function isConnected(
  node1: string,
  node2: string,
  adj: Map<string, Set<string>>,
): boolean {
  if (node1 === node2) return true;

  const visited = new Set<string>();
  const queue = [node1];
  visited.add(node1);

  while (queue.length > 0) {
    const current = queue.shift()!;
    if (current === node2) return true;

    const neighbors = adj.get(current) ?? new Set();
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor);
        queue.push(neighbor);
      }
    }
  }

  return false;
}

/**
 * Get all nodes reachable from a start node (undirected)
 */
function getConnectedComponent(
  startNode: string,
  adj: Map<string, Set<string>>,
): Set<string> {
  const component = new Set<string>();
  const queue = [startNode];
  component.add(startNode);

  while (queue.length > 0) {
    const current = queue.shift()!;
    const neighbors = adj.get(current) ?? new Set();

    for (const neighbor of neighbors) {
      if (!component.has(neighbor)) {
        component.add(neighbor);
        queue.push(neighbor);
      }
    }
  }

  return component;
}

/**
 * Topologically sort steps within a group using Kahn's algorithm.
 * Uses DIRECTED adjacency (A→B only if A produces material consumed by B).
 * Tie-break by creationOrder for determinism.
 */
function topologicalSort(
  stepIds: string[],
  operationNodes: GraphData['operationNodes'],
  directedAdj: Map<string, Set<string>>,
): string[] {
  const stepSet = new Set(stepIds);
  const inDegree = new Map<string, number>();
  const localAdj = new Map<string, Set<string>>();

  // Initialize local directed adjacency (only in-group edges)
  for (const stepId of stepIds) {
    inDegree.set(stepId, 0);
    localAdj.set(
      stepId,
      new Set((directedAdj.get(stepId) ?? new Set()).values()),
    );

    // Remove out-of-group neighbors
    const neighbors = localAdj.get(stepId)!;
    for (const neighbor of neighbors) {
      if (!stepSet.has(neighbor)) {
        neighbors.delete(neighbor);
      }
    }
  }

  // Calculate in-degrees based on DIRECTED edges
  for (const stepId of stepIds) {
    for (const neighbor of localAdj.get(stepId) ?? new Set()) {
      inDegree.set(neighbor, (inDegree.get(neighbor) ?? 0) + 1);
    }
  }

  // Kahn's algorithm with creationOrder tie-break
  const queue: string[] = [];
  for (const stepId of stepIds) {
    if (inDegree.get(stepId) === 0) {
      queue.push(stepId);
    }
  }

  // Sort by creationOrder for deterministic tie-break
  queue.sort((a, b) => {
    const orderA = operationNodes[a]?.creationOrder ?? 0;
    const orderB = operationNodes[b]?.creationOrder ?? 0;
    return orderA - orderB;
  });

  const sorted: string[] = [];

  while (queue.length > 0) {
    // Always take the lowest creationOrder from queue
    queue.sort((a, b) => {
      const orderA = operationNodes[a]?.creationOrder ?? 0;
      const orderB = operationNodes[b]?.creationOrder ?? 0;
      return orderA - orderB;
    });

    const current = queue.shift()!;
    sorted.push(current);

    for (const neighbor of localAdj.get(current) ?? new Set()) {
      inDegree.set(neighbor, (inDegree.get(neighbor) ?? 0) - 1);
      if (inDegree.get(neighbor) === 0) {
        queue.push(neighbor);
      }
    }
  }

  return sorted;
}

/**
 * Find all steps on paths between group members that are NOT in the group (gaps)
 */
function findMissingSteps(
  stepIds: string[],
  graphData: GraphData,
  adj: Map<string, Set<string>>,
): Set<string> {
  const stepSet = new Set(stepIds);
  const missing = new Set<string>();

  // For each pair of group members, find all steps on any path between them
  const { operationNodes } = graphData;

  for (let i = 0; i < stepIds.length; i++) {
    for (let j = i + 1; j < stepIds.length; j++) {
      const start = stepIds[i];
      const end = stepIds[j];

      // BFS to find all nodes on any path from start to end
      const onPath = new Set<string>();
      const queue: Array<{ node: string; visited: Set<string> }> = [
        { node: start, visited: new Set([start]) },
      ];

      while (queue.length > 0) {
        const { node, visited } = queue.shift()!;

        if (node === end) {
          for (const v of visited) {
            onPath.add(v);
          }
        }

        const neighbors = adj.get(node) ?? new Set();
        for (const neighbor of neighbors) {
          if (!visited.has(neighbor) && operationNodes[neighbor]) {
            const newVisited = new Set(visited);
            newVisited.add(neighbor);
            queue.push({ node: neighbor, visited: newVisited });
          }
        }
      }

      // Add any nodes on paths that are not in the group
      for (const node of onPath) {
        if (!stepSet.has(node)) {
          missing.add(node);
        }
      }
    }
  }

  return missing;
}

/**
 * Analyze a group subgraph for validity and return normalized order.
 *
 * Checks:
 * 1. All step IDs exist in operationNodes
 * 2. No duplicates
 * 3. All steps form one connected component
 * 4. No gaps (all steps on paths between members are included)
 *
 * Returns normalized (topologically sorted) order if valid.
 */
export function analyzeGroupSubgraph(
  stepIds: string[],
  graphData: GraphData,
  existingGroups?: Record<string, { stepIds: string[] }>,
): GroupAnalysisResult {
  const { operationNodes } = graphData;

  // Check for duplicates
  const idSet = new Set(stepIds);
  if (idSet.size !== stepIds.length) {
    return {
      ok: false,
      reason: 'duplicate',
    };
  }

  // Check all steps exist
  for (const stepId of stepIds) {
    if (!operationNodes[stepId]) {
      return {
        ok: false,
        reason: 'missing-step',
        missingStepIds: [stepId],
      };
    }
  }

  // Check for already-grouped steps (if checking against existing groups)
  if (existingGroups) {
    for (const stepId of stepIds) {
      for (const [groupId, group] of Object.entries(existingGroups)) {
        if (group.stepIds.includes(stepId)) {
          return {
            ok: false,
            reason: 'already-grouped',
            conflictingGroupId: groupId,
          };
        }
      }
    }
  }

  // Handle single step (trivially valid)
  if (stepIds.length === 1) {
    return { ok: true, ordered: stepIds };
  }

  // Build adjacency
  const adj = buildStepAdjacency(graphData);

  // Check connectivity
  for (let i = 1; i < stepIds.length; i++) {
    if (!isConnected(stepIds[0], stepIds[i], adj)) {
      return {
        ok: false,
        reason: 'disconnected',
      };
    }
  }

  // Check for gaps
  const missing = findMissingSteps(stepIds, graphData, adj);
  if (missing.size > 0) {
    return {
      ok: false,
      reason: 'gap',
      missingStepIds: Array.from(missing),
    };
  }

  // Topologically sort using DIRECTED adjacency and return
  const directedAdj = buildDirectedAdjacency(graphData);
  const ordered = topologicalSort(stepIds, operationNodes, directedAdj);
  return { ok: true, ordered };
}

/**
 * Validate single membership: no step belongs to multiple groups
 */
export function validateSingleMembership(
  stepId: string,
  groups: Record<string, { stepIds: string[] }>,
  excludeGroupId?: string,
): { valid: true } | { valid: false; conflictingGroupId: string } {
  for (const [groupId, group] of Object.entries(groups)) {
    if (excludeGroupId && groupId === excludeGroupId) continue;
    if (group.stepIds.includes(stepId)) {
      return { valid: false, conflictingGroupId: groupId };
    }
  }
  return { valid: true };
}
